import { createSelector } from '@reduxjs/toolkit';

// Base selectors
const getFunctionalSafetyState = (state) => state.persistedReducer.functionalSafetyReducer;

// Main assessment selectors
export const getFunctionalSafetyAssessment = createSelector(
  [getFunctionalSafetyState],
  (state) => state.functionalSafetyAssessment
);

export const getFunctionalSafetyAssessments = createSelector(
  [getFunctionalSafetyState],
  (state) => state.functionalSafetyAssessments
);

// Sub-assessments selectors
export const getMTTFAssessments = createSelector(
  [getFunctionalSafetyState],
  (state) => state.mttfAssessments
);

export const getDCavgAssessments = createSelector(
  [getFunctionalSafetyState],
  (state) => state.dcavgAssessments
);

export const getCCFAssessments = createSelector(
  [getFunctionalSafetyState],
  (state) => state.ccfAssessments
);

export const getHSRAssessments = createSelector(
  [getFunctionalSafetyState],
  (state) => state.hsrAssessments
);

// Static data selectors
export const getCCFOptions = createSelector(
  [getFunctionalSafetyState],
  (state) => state.ccfOptions
);

export const getHSROptions = createSelector(
  [getFunctionalSafetyState],
  (state) => state.hsrOptions
);

// Stats selector
export const getFunctionalSafetyStats = createSelector(
  [getFunctionalSafetyState],
  (state) => state.stats
);

export const getFunctionalSafetyStatsLoading = createSelector(
  [getFunctionalSafetyState],
  (state) => state.isLoadingStats
);

// Loading state selectors
export const getFunctionalSafetyLoading = createSelector(
  [getFunctionalSafetyState],
  (state) => state.isLoading
);

export const getFunctionalSafetyCreating = createSelector(
  [getFunctionalSafetyState],
  (state) => state.isCreating
);

export const getFunctionalSafetyUpdating = createSelector(
  [getFunctionalSafetyState],
  (state) => state.isUpdating
);

export const getFunctionalSafetyDeleting = createSelector(
  [getFunctionalSafetyState],
  (state) => state.isDeleting
);

// Error selector
export const getFunctionalSafetyError = createSelector(
  [getFunctionalSafetyState],
  (state) => state.error
);

// UI state selectors
export const getSelectedAssessment = createSelector(
  [getFunctionalSafetyState],
  (state) => state.selectedAssessment
);

export const getActiveModal = createSelector(
  [getFunctionalSafetyState],
  (state) => state.activeModal
);

// Computed selectors
export const getAssessmentById = createSelector(
  [getFunctionalSafetyAssessments, (_state, id) => id],
  (assessments, id) => assessments.find(assessment => assessment.id === id)
);

export const getMTTFAssessmentsByType = createSelector(
  [getMTTFAssessments, (_state, componentType) => componentType],
  (assessments, componentType) => assessments.filter(assessment => assessment.component_type === componentType)
);

export const getDCavgAssessmentsByMTTF = createSelector(
  [getDCavgAssessments, (_state, mttfId) => mttfId],
  (assessments, mttfId) => assessments.filter(assessment => assessment.mttf_assessment === mttfId)
);

export const getCCFAssessmentsByOption = createSelector(
  [getCCFAssessments, (_state, optionId) => optionId],
  (assessments, optionId) => assessments.filter(assessment => assessment.ccf_option === optionId)
);

export const getHSRAssessmentsByOption = createSelector(
  [getHSRAssessments, (_state, optionId) => optionId],
  (assessments, optionId) => assessments.filter(assessment => assessment.hsr_option === optionId)
);

// Assessment status selectors
export const getAssessmentCompletionStatus = createSelector(
  [getFunctionalSafetyAssessment],
  (assessment) => {
    if (!assessment) return null;

    return {
      mttf: assessment.mttf_assessments && assessment.mttf_assessments.length > 0,
      dcavg: assessment.dcavg_assessments && assessment.dcavg_assessments.length > 0,
      ccf: assessment.ccf_assessments && assessment.ccf_assessments.length > 0,
      hsr: assessment.hsr_assessments && assessment.hsr_assessments.length > 0,
    };
  }
);

export const getAssessmentScores = createSelector(
  [getFunctionalSafetyStats],
  (stats) => {
    if (!stats) return null;

    return {
      total_mttf_score: stats.total_mttf_score || 0,
      total_dcavg_score: stats.total_dcavg_score || 0,
      total_ccf_score: stats.total_ccf_score || 0,
      total_hsr_score: stats.total_hsr_score || 0,
    };
  }
);

// Modal state selectors
export const isModalOpen = createSelector(
  [getActiveModal, (_state, modalName) => modalName],
  (activeModal, modalName) => activeModal === modalName
);

export const getModalData = createSelector(
  [getFunctionalSafetyState],
  (state) => state.modalData
);
